import React, { useState, useEffect, useCallback } from 'react';
import { firestore } from '../firebase';
import { auth } from '../firebase';
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc,
  query, where,
} from 'firebase/firestore';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import './VideoJudgeQueue.css';

/** Firestore Timestamp or missing — for client-side sort without a composite index. */
function submittedAtMillis(data) {
  const t = data.submittedAt;
  if (!t) return 0;
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t.seconds === 'number') return t.seconds * 1000 + (t.nanoseconds || 0) / 1e6;
  return 0;
}

const VideoJudgeQueue = () => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [teams, setTeams] = useState({});
  const [puzzles, setPuzzles] = useState({});
  const [filterStatus, setFilterStatus] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [reviewState, setReviewState] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [snap, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        const evs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEvents(evs);
        const defaultId = resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: evs });
        setSelectedEventId(defaultId);
      } catch (err) {
        setError('Failed to load events: ' + err.message);
      }
    })();
  }, []);

  const loadSubmissions = useCallback(async () => {
    if (!selectedEventId) return;
    setLoading(true);
    setError('');
    try {
      // Equality-only query: no composite index (eventId + submittedAt) required.
      // Sort newest-first in memory — typical volume per event is small enough for this.
      const q = query(
        collection(firestore, 'videoSubmissions'),
        where('eventId', '==', selectedEventId)
      );
      const snap = await getDocs(q);
      const subs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => submittedAtMillis(b) - submittedAtMillis(a));

      // Batch-fetch teams and puzzles referenced
      const teamIds = [...new Set(subs.map((s) => s.teamId).filter(Boolean))];
      const puzzleIds = [...new Set(subs.map((s) => s.puzzleId).filter(Boolean))];

      const [teamDocs, puzzleDocs] = await Promise.all([
        Promise.all(teamIds.map((id) => getDoc(doc(firestore, 'teams', id)))),
        Promise.all(puzzleIds.map((id) => getDoc(doc(firestore, 'bonusPuzzles', id)))),
      ]);

      const teamsMap = {};
      teamDocs.forEach((d) => { if (d.exists()) teamsMap[d.id] = { id: d.id, ...d.data() }; });
      const puzzlesMap = {};
      puzzleDocs.forEach((d) => { if (d.exists()) puzzlesMap[d.id] = { id: d.id, ...d.data() }; });

      setTeams(teamsMap);
      setPuzzles(puzzlesMap);
      setSubmissions(subs);

      const initReview = {};
      subs.forEach((s) => {
        if (!reviewState[s.id]) {
          initReview[s.id] = { creativityPoints: 0, notes: '', saving: false };
        }
      });
      setReviewState((prev) => ({ ...initReview, ...prev }));
    } catch (err) {
      setError('Failed to load submissions: ' + err.message);
    }
    setLoading(false);
  }, [selectedEventId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const updateReview = (submissionId, field, value) => {
    setReviewState((prev) => ({
      ...prev,
      [submissionId]: { ...prev[submissionId], [field]: value },
    }));
  };

  const handleApprove = async (submission) => {
    const puzzle = puzzles[submission.puzzleId];
    if (!puzzle) return;

    const rs = reviewState[submission.id] || {};
    const basePoints = Number(puzzle.basePoints) || 0;
    const creativityPoints = Math.max(0, Math.min(Number(puzzle.creativityMaxPoints) || 0, Number(rs.creativityPoints) || 0));
    const totalValue = basePoints + creativityPoints;

    updateReview(submission.id, 'saving', true);
    try {
      const judgeUid = auth.currentUser?.uid || 'unknown';

      const bonusScoreRef = await addDoc(collection(firestore, 'bonusScores'), {
        eventId: submission.eventId,
        teamId: submission.teamId,
        puzzleId: submission.puzzleId,
        sponsorId: submission.sponsorId,
        value: totalValue,
        timestamp: new Date(),
      });

      await updateDoc(doc(firestore, 'videoSubmissions', submission.id), {
        status: 'approved',
        basePoints,
        creativityPoints,
        reviewedAt: new Date(),
        reviewedBy: judgeUid,
        bonusScoreId: bonusScoreRef.id,
      });

      setSubmissions((prev) =>
        prev.map((s) => s.id === submission.id
          ? { ...s, status: 'approved', basePoints, creativityPoints, bonusScoreId: bonusScoreRef.id }
          : s
        )
      );
    } catch (err) {
      setError('Approval failed: ' + err.message);
    }
    updateReview(submission.id, 'saving', false);
  };

  const handleReject = async (submission) => {
    const rs = reviewState[submission.id] || {};
    const notes = (rs.notes || '').trim();

    updateReview(submission.id, 'saving', true);
    try {
      const judgeUid = auth.currentUser?.uid || 'unknown';
      await updateDoc(doc(firestore, 'videoSubmissions', submission.id), {
        status: 'rejected',
        judgeNotes: notes,
        reviewedAt: new Date(),
        reviewedBy: judgeUid,
      });
      setSubmissions((prev) =>
        prev.map((s) => s.id === submission.id
          ? { ...s, status: 'rejected', judgeNotes: notes }
          : s
        )
      );
    } catch (err) {
      setError('Rejection failed: ' + err.message);
    }
    updateReview(submission.id, 'saving', false);
  };

  const filtered = submissions.filter((s) => {
    if (filterStatus === 'pending') return s.status === 'pending';
    return true;
  });

  const pendingCount = submissions.filter((s) => s.status === 'pending').length;

  return (
    <div className="vjq-root">
      <h2 className="vjq-title">Video Judge Queue</h2>

      <div className="vjq-controls">
        <div className="vjq-control-group">
          <label>Event:</label>
          <select
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="vjq-select"
          >
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.title || ev.id}</option>
            ))}
          </select>
        </div>
        <div className="vjq-control-group">
          <label>Show:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="vjq-select"
          >
            <option value="pending">Pending ({pendingCount})</option>
            <option value="all">All ({submissions.length})</option>
          </select>
        </div>
        <button onClick={loadSubmissions} className="vjq-refresh-btn" disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="vjq-error">{error}</div>}
      {loading && <p className="vjq-loading">Loading submissions…</p>}

      {!loading && filtered.length === 0 && (
        <p className="vjq-empty">
          {filterStatus === 'pending' ? 'No pending submissions.' : 'No submissions found.'}
        </p>
      )}

      <div className="vjq-list">
        {filtered.map((sub) => {
          const team = teams[sub.teamId];
          const puzzle = puzzles[sub.puzzleId];
          const rs = reviewState[sub.id] || { creativityPoints: 0, notes: '', saving: false };
          const maxCreativity = Number(puzzle?.creativityMaxPoints) || 0;
          const basePoints = Number(puzzle?.basePoints) || 0;
          const isApproved = sub.status === 'approved';
          const isRejected = sub.status === 'rejected';

          return (
            <div key={sub.id} className={`vjq-card vjq-card--${sub.status}`}>
              <div className="vjq-card-meta">
                <span className="vjq-meta-team">
                  Team {team?.teamNumber || sub.teamId}
                  {team?.mentor?.name ? ` — ${team.mentor.name}` : ''}
                </span>
                <span className="vjq-meta-sponsor">{sub.sponsorName}</span>
                <span className={`vjq-status-badge vjq-status--${sub.status}`}>{sub.status}</span>
                <span className="vjq-meta-puzzle">
                  {puzzle ? `Puzzle ${puzzle.number}: ${puzzle.title}` : sub.puzzleId}
                </span>
                {sub.submittedAt && (
                  <span className="vjq-meta-time">
                    Submitted {new Date(sub.submittedAt.seconds ? sub.submittedAt.seconds * 1000 : sub.submittedAt).toLocaleString()}
                  </span>
                )}
              </div>

              <div className="vjq-video-wrap">
                <video
                  src={sub.videoUrl}
                  controls
                  className="vjq-video"
                  playsInline
                />
                <a
                  href={sub.videoUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vjq-download-link"
                >
                  Download video (.mov / .mp4)
                </a>
              </div>

              {isApproved && (
                <div className="vjq-approved-summary">
                  Approved — {sub.basePoints} base + {sub.creativityPoints} creativity = <strong>{sub.basePoints + sub.creativityPoints} pts</strong>
                  <div className="vjq-export-social">
                    <a href={sub.videoUrl} download className="vjq-social-btn">Download for social</a>
                    <button
                      className="vjq-social-btn"
                      onClick={() => {
                        navigator.clipboard?.writeText(
                          `Thank you to our amazing Fresh Meet sponsor ${sub.sponsorName}! 🎉 #FreshMeet #Skydiving`
                        );
                      }}
                    >
                      Copy caption
                    </button>
                  </div>
                </div>
              )}

              {isRejected && !isApproved && (
                <div className="vjq-rejected-summary">
                  Rejected — Feedback: {sub.judgeNotes || '(none)'}
                </div>
              )}

              {!isApproved && (
                <div className="vjq-review-form">
                  <div className="vjq-review-row">
                    <label className="vjq-review-label">
                      Creativity bonus (0–{maxCreativity} pts):
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={maxCreativity}
                      value={rs.creativityPoints}
                      onChange={(e) => updateReview(sub.id, 'creativityPoints', e.target.value)}
                      className="vjq-points-input"
                    />
                    <span className="vjq-points-preview">
                      Total if approved: {basePoints + Math.min(maxCreativity, Number(rs.creativityPoints) || 0)} pts
                    </span>
                  </div>
                  <div className="vjq-review-row">
                    <label className="vjq-review-label">Rejection notes (shown to team):</label>
                    <textarea
                      value={rs.notes}
                      onChange={(e) => updateReview(sub.id, 'notes', e.target.value)}
                      rows={2}
                      className="vjq-notes-input"
                      placeholder="Optional — what should the team fix?"
                    />
                  </div>
                  <div className="vjq-action-row">
                    <button
                      className="vjq-btn vjq-btn--approve"
                      onClick={() => handleApprove(sub)}
                      disabled={rs.saving}
                    >
                      {rs.saving ? 'Saving…' : 'Approve'}
                    </button>
                    <button
                      className="vjq-btn vjq-btn--reject"
                      onClick={() => handleReject(sub)}
                      disabled={rs.saving}
                    >
                      {rs.saving ? 'Saving…' : 'Reject'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VideoJudgeQueue;
